import { type ReactElement, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import type { DataGridProProps, GridColDef, GridColumnVisibilityModel, GridValidRowModel } from '@mui/x-data-grid-pro';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { getItemFromSessionStorage } from '../../utils/storageHelper';
import type { StyleObj } from '../../@types';
import { COLUMN_FIELDS } from '../../constants/table';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../constants';
import { createDateColumn } from '../../helpers/columns';
import { defaultColumnsCryptoStaticWallets } from '../../helpers/table';
import { usePagination } from '../../hooks/usePagination';
import { createColumn } from '../../utils';
import {
  type CryptoAudit,
  type CryptoCurrency,
  type CryptoStaticWallet,
  type CryptoSection,
  useCryptoList,
  useCryptoOverview,
  useRefreshCryptoCurrencies,
  useUpdateCryptoCurrency,
} from '../../queries/cryptoDashboard';
import {
  cryptoCurrencySettingsSchema,
  cryptoCurrencyFormSchema,
  type CryptoCurrencyForm,
} from '../../schema/cryptoDashboard';
import TableTemplate from '../templates/TableTemplate';
import CryptoCoinInfo from '../organisms/CryptoCoinInfo';
import CryptoWalletFilters from '../organisms/CryptoWalletFilters';
import CryptoWalletDetails from '../organisms/CryptoWalletDetails';
import CryptoOperationalOverview from '../organisms/CryptoOperationalOverview';

const styles: StyleObj = {
  page: { width: '100%', minWidth: 0 },
  settingsPanel: { p: 2 },
  summaryCard: { p: 2, flex: 1 },
  refreshOverview: { alignSelf: 'flex-start' },
  table: { height: 600 },
};

// The dashboard API accepts at most 100 rows per request.
const dashboardPageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS.filter((size) => size <= 100);

const walletColumns: GridColDef<CryptoStaticWallet>[] = [
  createColumn(COLUMN_FIELDS.CURRENCY, 'Currency', { minWidth: 120 }),
  createColumn('state', 'State', { minWidth: 120 }),
  createColumn(COLUMN_FIELDS.USERNAME, 'Username', { minWidth: 180 }),
  createColumn(COLUMN_FIELDS.EMAIL, 'Email', { minWidth: 250 }),
  createColumn('playerId', 'Player ID', { minWidth: 200 }),
  createColumn('clientId', 'Client', { minWidth: 200 }),
  createColumn('merchantId', 'Merchant', { minWidth: 180 }),
  createColumn(COLUMN_FIELDS.ADDRESS, 'Address', { minWidth: 300 }),
  createColumn('tag', 'Tag', { minWidth: 130 }),
  createDateColumn(COLUMN_FIELDS.CREATED_AT, 'Created'),
  createDateColumn('updatedAt', 'Updated'),
];

const auditColumns: GridColDef<CryptoAudit>[] = [
  createDateColumn(COLUMN_FIELDS.CREATED_AT, 'Time'),
  createColumn('actorId', 'Actor', { minWidth: 200 }),
  createColumn('action', 'Action', { minWidth: 200 }),
  createColumn('reason', 'Reason', { minWidth: 300 }),
  createColumn('before', 'Before', { minWidth: 300, valueFormatter: (value: unknown) => JSON.stringify(value) }),
  createColumn('after', 'After', { minWidth: 300, valueFormatter: (value: unknown) => JSON.stringify(value) }),
];

// Both grids use renderCell; desktop-only boolean column rendering is invisible in mobile cards.
const renderBoolean: GridColDef<CryptoCurrency, boolean>['renderCell'] = ({ value }) => (value ? 'Yes' : 'No');

const displayEffective = (value: boolean | undefined) => {
  if (value === undefined) return 'Unavailable';
  return value ? 'Yes' : 'No';
};

const currencyColumns: GridColDef<CryptoCurrency>[] = [
  createColumn(COLUMN_FIELDS.CURRENCY, 'Currency', { minWidth: 140 }),
  createColumn('providerActive', 'Provider active', { type: 'boolean', minWidth: 150, renderCell: renderBoolean }),
  createColumn('depositEnabled', 'Deposits enabled', { type: 'boolean', minWidth: 160, renderCell: renderBoolean }),
  createColumn('withdrawalEnabled', 'Withdrawals enabled', {
    type: 'boolean',
    minWidth: 180,
    renderCell: renderBoolean,
  }),
  createColumn('effectiveDepositEnabled', 'Effective deposits', {
    minWidth: 180,
    renderCell: ({ row }) => displayEffective(row.effectiveDepositEnabled),
  }),
  createColumn('effectiveWithdrawalEnabled', 'Effective withdrawals', {
    minWidth: 180,
    renderCell: ({ row }) => displayEffective(row.effectiveWithdrawalEnabled),
  }),
  createColumn('depositReasons', 'Deposit restrictions', {
    minWidth: 220,
    valueFormatter: (value: string[] | undefined) => value?.join(', ') ?? 'Unavailable',
  }),
  createColumn('withdrawalReasons', 'Withdrawal restrictions', {
    minWidth: 220,
    valueFormatter: (value: string[] | undefined) => value?.join(', ') ?? 'Unavailable',
  }),
  createColumn('symbol', 'Symbol', { minWidth: 120 }),
  createColumn('network', 'Network', {
    minWidth: 130,
    valueFormatter: (value: string | null | undefined) => value ?? 'Native',
  }),
  createColumn('platformDepositSupported', 'Supported deposits', {
    minWidth: 180,
    renderCell: ({ row }) => displayEffective(row.platformDepositSupported),
  }),
  createColumn('platformWithdrawalSupported', 'Supported withdrawals', {
    minWidth: 190,
    renderCell: ({ row }) => displayEffective(row.platformWithdrawalSupported),
  }),
  createColumn('tagRequired', 'Tag required', { minWidth: 130, renderCell: renderBoolean }),
  createColumn('minimumDeposit', 'Minimum deposit', { minWidth: 170 }),
  createColumn('minimumWithdrawal', 'Minimum withdrawal', { minWidth: 180 }),
  createColumn('networkFee', 'Network fee', { minWidth: 150 }),
];

const CurrencySettings = ({
  marketplaceId,
  currency,
  onClose,
}: {
  marketplaceId: string;
  currency: CryptoCurrency;
  onClose: () => void;
}) => {
  const mutation = useUpdateCryptoCurrency(marketplaceId);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CryptoCurrencyForm>({
    resolver: zodResolver(cryptoCurrencyFormSchema),
    defaultValues: {
      currency: currency.currency,
      depositEnabled: currency.depositEnabled,
      withdrawalEnabled: currency.withdrawalEnabled,
      reason: '',
      expectedVersion: currency.policyVersion,
      confirmed: false,
    },
  });

  const canEnable = currency.providerActive && !currency.catalogStale;
  const canEnableDeposit = canEnable && currency.platformDepositSupported !== false && currency.currency !== 'XRP';
  const canEnableWithdrawal = canEnable && currency.platformWithdrawalSupported !== false;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(cryptoCurrencySettingsSchema.parse(values));
      onClose();
    } catch {
      /* The mutation error is shown below. */
    }
  });

  return (
    <Paper variant='outlined' sx={styles.settingsPanel}>
      <Stack component='form' spacing={2} onSubmit={onSubmit}>
        <Typography variant='h6'>Local settings: {currency.currency}</Typography>
        <Typography variant='body2'>
          These settings change local currency availability. They do not change provider settings or initiate a payment.
          Previously issued addresses can still receive funds; callbacks and settlement continue.
        </Typography>
        {!canEnable && (
          <Alert severity='warning'>
            This currency is unavailable. You can turn off existing settings, but cannot enable them.
          </Alert>
        )}
        <Controller
          name='depositEnabled'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value}
                  onChange={(_, checked) => field.onChange(checked)}
                  disabled={mutation.isPending || (!canEnableDeposit && !field.value)}
                />
              }
              label='Enable deposits'
            />
          )}
        />
        <Controller
          name='withdrawalEnabled'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={field.value}
                  onChange={(_, checked) => field.onChange(checked)}
                  disabled={mutation.isPending || (!canEnableWithdrawal && !field.value)}
                />
              }
              label='Enable withdrawals'
            />
          )}
        />
        <TextField
          label='Reason'
          {...register('reason')}
          error={!!errors.reason}
          helperText={errors.reason?.message}
          disabled={mutation.isPending}
        />
        <Controller
          name='confirmed'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              label='I confirm these currency settings'
              control={
                <Checkbox
                  checked={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  disabled={mutation.isPending}
                />
              }
            />
          )}
        />
        {errors.confirmed && <Alert severity='error'>{errors.confirmed.message}</Alert>}
        {mutation.isError && (
          <Alert severity='error'>Could not save currency settings. Check the response and try again.</Alert>
        )}
        <Stack direction='row' spacing={1}>
          <Button type='submit' variant='contained' disabled={mutation.isPending}>
            Save local settings
          </Button>
          <Button onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

const Overview = ({ marketplaceId }: { marketplaceId: string }) => {
  const query = useCryptoOverview(marketplaceId);

  return (
    <Stack spacing={2}>
      <Button sx={styles.refreshOverview} onClick={() => void query.refetch()} disabled={query.isFetching}>
        Refresh overview
      </Button>
      {query.isPending && <Typography>Loading overview…</Typography>}
      {query.isError && <Alert severity='error'>Could not load overview.</Alert>}
      <CryptoOperationalOverview marketplaceId={marketplaceId} />
      {query.data && (
        <>
          <Typography>Merchant: {query.data.merchantId}</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Paper variant='outlined' sx={styles.summaryCard}>
              <Typography variant='h6'>Static wallets</Typography>
              {Object.entries(query.data.staticWallets).map(([status, count]) => (
                <Typography key={status}>
                  {status.replaceAll('_', ' ')}: {count}
                </Typography>
              ))}
            </Paper>
            <Paper variant='outlined' sx={styles.summaryCard}>
              <Typography variant='h6'>Deposit callbacks</Typography>
              <Typography variant='body2'>
                Callback processing counts; these do not confirm ledger settlement.
              </Typography>
              {Object.entries(query.data.deposits).map(([status, count]) => (
                <Typography key={status}>
                  {status.replaceAll('_', ' ')}: {count}
                </Typography>
              ))}
            </Paper>
          </Stack>
        </>
      )}
    </Stack>
  );
};

type DashboardTableProps<Row extends GridValidRowModel> = {
  query: ReturnType<typeof usePagination<Row>>;
  section: CryptoSection;
  columns: GridColDef<Row>[];
  getRowId?: DataGridProProps<Row>['getRowId'];
  defaultVisibleColumns?: GridColumnVisibilityModel;
  visibilityStorageKey?: string;
  refreshAction?: ReactElement;
};

const DashboardTable = <Row extends GridValidRowModel>({
  query,
  section,
  columns,
  getRowId,
  defaultVisibleColumns,
  visibilityStorageKey,
  refreshAction,
}: DashboardTableProps<Row>) => {
  const paginationModel = useMemo(
    () => ({ page: query.activePage - 1, pageSize: query.limit }),
    [query.activePage, query.limit]
  );

  const handleSearch = (search: string | null) => query.changeQuery({ search });

  const refreshButton = (
    <Button onClick={() => void query.refetch()} disabled={query.isFetching}>
      Refresh
    </Button>
  );

  return (
    <Stack spacing={2}>
      {query.status === 'error' && <Alert severity='error'>Could not load {section}. Use Refresh to try again.</Alert>}
      <Box sx={styles.table}>
        <TableTemplate<Row>
          rows={query.data?.items ?? []}
          rowCount={query.data?.count ?? 0}
          columns={columns}
          getRowId={getRowId}
          loading={query.isFetching}
          paginationModel={paginationModel}
          pageSizeOptions={dashboardPageSizeOptions}
          handlePaginationModelChange={query.updateQueryParams}
          handleSearch={handleSearch}
          showFilter={false}
          disableColumnSorting
          customTableHeaderContent={refreshAction ?? refreshButton}
          defaultVisibleColumns={defaultVisibleColumns}
          visibilityStorageKey={visibilityStorageKey}
        />
      </Box>
    </Stack>
  );
};

const CurrencyList = ({ marketplaceId }: { marketplaceId: string }) => {
  const query = useCryptoList(marketplaceId, 'currencies');
  const refresh = useRefreshCryptoCurrencies(marketplaceId);
  const isRefreshing = query.isFetching || refresh.isPending;
  const catalogStale = query.data?.items.some((item) => item.catalogStale);
  const providerFetchedAt = query.data?.items[0]?.providerFetchedAt;
  const [editingCurrency, setEditingCurrency] = useState<CryptoCurrency | null>(null);
  const [infoCurrencyCode, setInfoCurrencyCode] = useState<string | null>(null);
  const infoCurrency = query.data?.items.find((item) => item.currency === infoCurrencyCode);

  const columns = useMemo<GridColDef<CryptoCurrency>[]>(
    () => [
      ...currencyColumns,
      createColumn(COLUMN_FIELDS.ACTIONS, 'Settings', {
        minWidth: 280,
        sortable: false,
        renderCell: ({ row }: { row: CryptoCurrency }) => (
          <Stack direction='row'>
            <Button onClick={() => setInfoCurrencyCode(row.currency)}>View coin info</Button>
            <Button onClick={() => setEditingCurrency(row)}>Edit settings</Button>
          </Stack>
        ),
      }),
    ],
    []
  );

  return (
    <Stack spacing={2}>
      {providerFetchedAt && <Typography variant='body2'>Provider catalog checked: {providerFetchedAt}</Typography>}
      {catalogStale && (
        <Alert severity='warning'>
          Showing a previously fetched provider catalog. Use Refresh to request fresh data.
        </Alert>
      )}
      {refresh.isError && (
        <Alert severity='error'>Could not refresh the provider catalog. Previous values are still shown.</Alert>
      )}
      {infoCurrency && (
        <CryptoCoinInfo
          currency={infoCurrency}
          isRefreshing={isRefreshing}
          isError={refresh.isError || query.status === 'error'}
          onRefresh={() => refresh.mutate()}
          onClose={() => setInfoCurrencyCode(null)}
        />
      )}
      {editingCurrency && (
        <CurrencySettings
          key={editingCurrency.currency}
          marketplaceId={marketplaceId}
          currency={editingCurrency}
          onClose={() => setEditingCurrency(null)}
        />
      )}
      <DashboardTable
        query={query}
        section='currencies'
        columns={columns}
        getRowId={(row) => row.currency}
        refreshAction={
          <Button onClick={() => refresh.mutate()} disabled={isRefreshing}>
            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      />
    </Stack>
  );
};

const StaticWalletList = ({
  marketplaceId,
  onViewAudit,
}: {
  marketplaceId: string;
  onViewAudit: (clientId: string) => void;
}) => {
  const query = useCryptoList(marketplaceId, 'static-wallets');
  const [walletId, setWalletId] = useState<string | null>(null);
  const columns = useMemo<GridColDef<CryptoStaticWallet>[]>(
    () => [
      ...walletColumns,
      createColumn(COLUMN_FIELDS.ACTIONS, 'Details', {
        minWidth: 150,
        renderCell: ({ row }) => <Button onClick={() => setWalletId(row.id)}>View wallet</Button>,
      }),
    ],
    []
  );
  return (
    <Stack spacing={2}>
      <CryptoWalletFilters
        onApply={(filters) => {
          query.changeQuery(filters);
          setWalletId(null);
        }}
      />
      {walletId && (
        <CryptoWalletDetails
          key={walletId}
          marketplaceId={marketplaceId}
          walletId={walletId}
          onViewAudit={onViewAudit}
          onClose={() => setWalletId(null)}
        />
      )}
      <DashboardTable
        query={query}
        section='static-wallets'
        columns={columns}
        visibilityStorageKey='crypto-dashboard-static-wallets-columns'
        defaultVisibleColumns={{ ...defaultColumnsCryptoStaticWallets, actions: true }}
      />
    </Stack>
  );
};

const AuditList = ({
  marketplaceId,
  search,
  onClear,
}: {
  marketplaceId: string;
  search: string;
  onClear: () => void;
}) => {
  const query = useCryptoList(marketplaceId, 'audit', search);

  return (
    <Stack spacing={2}>
      {search && (
        <Alert severity='info'>
          Client audit filter: {search} <Button onClick={onClear}>Clear client audit filter</Button>
        </Alert>
      )}
      <DashboardTable query={query} section='audit' columns={auditColumns} />
    </Stack>
  );
};

const SuperAdminCryptoDashboardPage = () => {
  const marketplace = getItemFromSessionStorage('marketplace');
  const [auditSearch, setAuditSearch] = useState('');
  const [section, setSection] = useState<'overview' | CryptoSection>('overview');

  if (!marketplace?.id) return <Alert severity='info'>Select a marketplace to view 0xProcessing.</Alert>;

  return (
    <Stack spacing={2} sx={styles.page}>
      <Typography variant='h5'>0xProcessing</Typography>
      <Typography>Marketplace: {marketplace.name}</Typography>
      <Alert severity='info'>
        Currency controls affect local availability. Withdrawals remain subject to backend capability and marketplace
        settings.
      </Alert>
      <Tabs
        value={section}
        onChange={(_, value: 'overview' | CryptoSection) => setSection(value)}
        variant='scrollable'
        allowScrollButtonsMobile
        aria-label='0xProcessing sections'
      >
        <Tab value='overview' label='Overview' />
        <Tab value='currencies' label='Currencies' />
        <Tab value='static-wallets' label='Static wallets' />
        <Tab value='audit' label='Audit' />
      </Tabs>
      {section === 'overview' && <Overview marketplaceId={marketplace.id} />}
      {section === 'currencies' && <CurrencyList key={marketplace.id} marketplaceId={marketplace.id} />}
      {section === 'static-wallets' && (
        <StaticWalletList
          key={marketplace.id}
          marketplaceId={marketplace.id}
          onViewAudit={(clientId) => {
            setAuditSearch(clientId);
            setSection('audit');
          }}
        />
      )}
      {section === 'audit' && (
        <AuditList
          key={marketplace.id + auditSearch}
          marketplaceId={marketplace.id}
          search={auditSearch}
          onClear={() => setAuditSearch('')}
        />
      )}
    </Stack>
  );
};

export default SuperAdminCryptoDashboardPage;
